import { FC } from 'react';

import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { DrawerSection } from 'components/drawerSections/DrawerSections';
import RaaTextAreaField from 'components/fieldComponents/raaTextAreaField/RaaTextAreaField';
import { Colors } from 'components/map/Styles';
import { penCircleSvg } from 'components/map/icons';
import { ExternalLink } from 'components/sharedElements/sharedElements';

import { BebyggelseobjektUtkast, LanKommun, Socken } from 'models';

import { getAndamalString } from 'utilities/getAndamalString';

const FastighetsinformationSection: FC<{ utkastData: BebyggelseobjektUtkast | undefined; dirty: boolean }> = ({
  utkastData,
  dirty
}) => {
  const { setDialog, unsetDialog } = useDialog();

  return (
    <>
      {utkastData && (
        <DrawerSection>
          <div className="tw-mb-2 tw-flex tw-text-raa-green">
            <img
              src={`data:image/svg+xml,${encodeURIComponent(penCircleSvg(Colors.GREEN))}`}
              alt="Ikon för ej publicerat utkast"
              className="tw-mr-2 tw-h-6"
            />
            <span className="tw-grow">
              Utkast {utkastData.aktivitetref?.aktivitetsnamn && <>({utkastData.aktivitetref?.aktivitetsnamn})</>}
            </span>
            <ExternalLink
              href={`${window.location.href.replace('/redigera', '')}`}
              text="Förhandsvisa"
              beforeNavigation={
                dirty
                  ? () => {
                      setDialog(RaaDialog, {
                        header: 'Du har osparade ändringar',
                        close: unsetDialog,
                        cancel: unsetDialog,
                        cancelButtonText: 'stäng',
                        ariaDescribedBy: 'dialog-description',
                        children: (
                          <p id="dialog-description" className="tw-mt-8">
                            Spara dina ändringar för att förhandsvisa
                          </p>
                        )
                      });
                    }
                  : undefined
              }
            />
          </div>
          <h1 className="tw-heading-lg">{utkastData.titel}</h1>
          <p className="tw-mb-4 tw-mt-2">
            Utkast av: {utkastData.registrering.anvandarnamn}{' '}
            {new Date(utkastData.registrering.senast_andrad).toLocaleDateString('sv-SE')}
          </p>

          <table className="tw-w-full">
            <tbody className="tw-w-full">
              <tr>
                <th className="tw-text-left">Bebyggelsenummer</th>
                <td>{utkastData.bebyggelsenummer}</td>
              </tr>
              {utkastData.bebyggelseobjektgrupp === 'ENSKILT_BEBYGGELSEOBJEKT' && (
                <>
                  <tr>
                    <th className="tw-text-left">Fastighet</th>
                    <td className="tw-capitalize">{utkastData?.berorda_fastigheter?.join(', ').toLowerCase()}</td>
                  </tr>
                  <>
                    <tr>
                      <th className="tw-text-left">Husnummer</th>
                      <td>{utkastData?.lm_registerbyggnad?.husnummer || '–'}</td>
                    </tr>
                    <tr>
                      <th className="tw-text-left">Nuv. ändamål enl. LM</th>
                      <td>{getAndamalString(utkastData?.lm_registerbyggnad?.byggnadsandamal || []) || '–'}</td>
                    </tr>
                  </>
                </>
              )}

              <tr>
                <th className="tw-text-left">Socken, Kommun, Län</th>
                {/*TODO!! Hur ska vi presentera om det kommer flera?*/}
                <td>
                  {utkastData.geografisk_indelning.socken.map((socken: Socken) => socken.socken_namn).join(', ')},{' '}
                  {utkastData.geografisk_indelning.kommun.map((kommun: LanKommun) => kommun.kommun_namn).join(', ')},{' '}
                  {utkastData.geografisk_indelning.kommun.map((kommun: LanKommun) => kommun.lan_namn).join(', ')}
                </td>
              </tr>
            </tbody>
          </table>

          <RaaTextAreaField label="Intern anteckning (raderas vid publicering)" name="anteckning" />
        </DrawerSection>
      )}
    </>
  );
};

export default FastighetsinformationSection;
